/**
 * Drone Mission Store
 * 
 * Manages drone mission visualization on the map:
 * - Waypoints display
 * - Home point selection
 * - Flight path visualization
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface DroneWaypoint {
  lat: number;
  lng: number;
  altitude: number;
  index: number;
}

export interface DroneSegment {
  waypoints: DroneWaypoint[];
  segmentIndex: number;
}

export interface WebODMTask {
  projectId: number;
  taskId: string;
  name: string;
  bounds?: {
    minLon: number;
    minLat: number;
    maxLon: number;
    maxLat: number;
    center: [number, number];
  };
  hasOrthophoto?: boolean;
  hasDsm?: boolean;
  coveragePct?: number;  // How much of the flight area this covers
}

interface DroneState {
  // Home point selection
  homePoint: [number, number] | null;  // [lat, lng]
  isSelectingHomePoint: boolean;
  
  // Waypoints display
  showWaypoints: boolean;
  waypoints: DroneWaypoint[];
  segments: DroneSegment[];
  
  // Current mission info
  currentPlotId: number | null;
  currentStage: 1 | 2 | null;
  flightSpeed: number;  // m/s
  
  // Orthophoto layer (WebODM)
  showOrthophoto: boolean;
  orthophotoTask: WebODMTask | null;  // Currently selected (for terrain following)
  orthophotoOpacity: number;
  
  // Multi-orthophoto support
  allOrthophotoTasks: WebODMTask[];  // All available tasks
  showAllOrthophotos: boolean;  // Show all layers at once
  orthophotoLayersEnabled: Record<string, boolean>;  // taskId -> enabled
  
  // DSM layer (WebODM)
  showDsm: boolean;
  dsmOpacity: number;
  
  // DSM coverage visualization
  showDsmCoverage: boolean;
  dsmCoveragePolygon: [number, number][] | null;  // [lat, lng][] polygon coordinates
  
  // Custom flight area (user-drawn polygon)
  customFlightArea: [number, number][] | null;  // [lat, lng][] polygon coordinates
  isDrawingFlightArea: boolean;
  useCustomFlightArea: boolean;  // Toggle to use custom area vs full parcel
  
  // Actions
  setHomePoint: (point: [number, number] | null) => void;
  startHomePointSelection: () => void;
  stopHomePointSelection: () => void;
  
  setShowWaypoints: (show: boolean) => void;
  setWaypoints: (waypoints: DroneWaypoint[]) => void;
  setSegments: (segments: DroneSegment[]) => void;
  
  setCurrentMission: (plotId: number | null, stage: 1 | 2 | null) => void;
  setFlightSpeed: (speed: number) => void;
  
  // Orthophoto actions
  setShowOrthophoto: (show: boolean) => void;
  setOrthophotoTask: (task: WebODMTask | null) => void;
  setOrthophotoOpacity: (opacity: number) => void;
  setShowDsm: (show: boolean) => void;
  setDsmOpacity: (opacity: number) => void;
  
  // Multi-orthophoto actions
  setAllOrthophotoTasks: (tasks: WebODMTask[]) => void;
  setShowAllOrthophotos: (show: boolean) => void;
  toggleOrthophotoLayer: (taskId: string, enabled: boolean) => void;
  enableAllOrthophotoLayers: () => void;
  
  // DSM coverage actions
  setShowDsmCoverage: (show: boolean) => void;
  setDsmCoveragePolygon: (polygon: [number, number][] | null) => void;
  
  // Custom flight area actions
  setCustomFlightArea: (polygon: [number, number][] | null) => void;
  startDrawingFlightArea: () => void;
  stopDrawingFlightArea: () => void;
  setUseCustomFlightArea: (use: boolean) => void;
  clearCustomFlightArea: () => void;
  
  clearDroneData: () => void;
}

export const useDroneStore = create<DroneState>()(
  devtools(
    (set) => ({
      // Initial state
      homePoint: null,
      isSelectingHomePoint: false,
      
      showWaypoints: false,
      waypoints: [],
      segments: [],
      
      currentPlotId: null,
      currentStage: null,
      flightSpeed: 5,
      
      // Orthophoto layer
      showOrthophoto: false,
      orthophotoTask: null,
      orthophotoOpacity: 0.9,
      
      // Multi-orthophoto support
      allOrthophotoTasks: [],
      showAllOrthophotos: false,
      orthophotoLayersEnabled: {},
      
      // DSM layer
      showDsm: false,
      dsmOpacity: 0.7,
      
      // DSM coverage
      showDsmCoverage: true,  // On by default when DSM is selected
      dsmCoveragePolygon: null,
      
      // Custom flight area
      customFlightArea: null,
      isDrawingFlightArea: false,
      useCustomFlightArea: false,
      
      // Actions
      setHomePoint: (point) => set({ 
        homePoint: point,
        isSelectingHomePoint: false,  // Stop selection mode when point is set
      }),
      
      startHomePointSelection: () => set({ 
        isSelectingHomePoint: true 
      }),
      
      stopHomePointSelection: () => set({ 
        isSelectingHomePoint: false 
      }),
      
      setShowWaypoints: (show) => set({ showWaypoints: show }),
      
      setWaypoints: (waypoints) => set({ waypoints }),
      
      setSegments: (segments) => set({ segments }),
      
      setCurrentMission: (plotId, stage) => set({ 
        currentPlotId: plotId,
        currentStage: stage,
      }),
      
      setFlightSpeed: (speed) => set({ flightSpeed: speed }),
      
      // Orthophoto actions
      setShowOrthophoto: (show) => set({ showOrthophoto: show }),
      
      setOrthophotoTask: (task) => set({ orthophotoTask: task }),
      
      setOrthophotoOpacity: (opacity) => set({ orthophotoOpacity: opacity }),
      
      setShowDsm: (show) => set({ showDsm: show }),
      
      setDsmOpacity: (opacity) => set({ dsmOpacity: opacity }),
      
      // Multi-orthophoto actions
      setAllOrthophotoTasks: (tasks) => {
        // Enable all layers by default
        const enabled: Record<string, boolean> = {};
        tasks.forEach(t => { enabled[t.taskId] = true; });
        set({ 
          allOrthophotoTasks: tasks,
          orthophotoLayersEnabled: enabled,
        });
      },
      
      setShowAllOrthophotos: (show) => set({ showAllOrthophotos: show }),
      
      toggleOrthophotoLayer: (taskId, enabled) => set((state) => ({
        orthophotoLayersEnabled: {
          ...state.orthophotoLayersEnabled,
          [taskId]: enabled,
        },
      })),
      
      enableAllOrthophotoLayers: () => set((state) => {
        const enabled: Record<string, boolean> = {};
        state.allOrthophotoTasks.forEach(t => { enabled[t.taskId] = true; });
        return { orthophotoLayersEnabled: enabled };
      }),
      
      // DSM coverage actions
      setShowDsmCoverage: (show) => set({ showDsmCoverage: show }),
      
      setDsmCoveragePolygon: (polygon) => set({ dsmCoveragePolygon: polygon }),
      
      // Custom flight area actions
      setCustomFlightArea: (polygon) => set({ 
        customFlightArea: polygon,
        isDrawingFlightArea: false,
        useCustomFlightArea: polygon !== null && polygon.length >= 3,  // Auto-enable when polygon is drawn
      }),
      
      startDrawingFlightArea: () => set({ 
        isDrawingFlightArea: true,
        customFlightArea: null,  // Clear previous area when starting new drawing
      }),
      
      stopDrawingFlightArea: () => set({ 
        isDrawingFlightArea: false,
      }),
      
      setUseCustomFlightArea: (use) => set({ useCustomFlightArea: use }),
      
      clearCustomFlightArea: () => set({ 
        customFlightArea: null,
        useCustomFlightArea: false,
      }),
      
      clearDroneData: () => set({
        homePoint: null,
        isSelectingHomePoint: false,
        showWaypoints: false,
        waypoints: [],
        segments: [],
        currentPlotId: null,
        currentStage: null,
        // NOTE: Do NOT reset orthophoto settings - they should persist
        // showOrthophoto, orthophotoTask, allOrthophotoTasks, etc. stay as-is
        showDsm: false,
        dsmCoveragePolygon: null,
        customFlightArea: null,
        isDrawingFlightArea: false,
        useCustomFlightArea: false,
      }),
    }),
    { name: 'DroneStore' }
  )
);

// Selectors
export const selectHomePoint = (state: DroneState) => state.homePoint;
export const selectIsSelectingHomePoint = (state: DroneState) => state.isSelectingHomePoint;
export const selectShowWaypoints = (state: DroneState) => state.showWaypoints;
export const selectWaypoints = (state: DroneState) => state.waypoints;
export const selectSegments = (state: DroneState) => state.segments;
export const selectShowOrthophoto = (state: DroneState) => state.showOrthophoto;
export const selectOrthophotoTask = (state: DroneState) => state.orthophotoTask;
export const selectOrthophotoOpacity = (state: DroneState) => state.orthophotoOpacity;
export const selectShowDsm = (state: DroneState) => state.showDsm;
export const selectDsmOpacity = (state: DroneState) => state.dsmOpacity;
